import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Steps, message, Divider, Alert, Image, Switch, Row, Col, Radio, Space, Modal, Typography } from 'antd';
import { PlusOutlined, DollarOutlined, CreditCardOutlined, WechatOutlined, AlipayOutlined, QqOutlined } from '@ant-design/icons';
import api from '../../services/api';
import ServerInstallProgress from '../../components/ServerInstallProgress';
import { useAuth } from '../../contexts/AuthContext';

const { Option } = Select;
const { Step } = Steps;
const { Title, Text } = Typography;

interface GameType {
  id: number;
  name: string;
  display_name: string;
  description: string;
  icon?: string;
  background_image_url?: string;
  supports_mods?: boolean;
  is_steam_game?: boolean;
}

interface ServerNode {
  id: number;
  name: string;
  host: string;
  status: string;
  is_local: boolean;
  server_path: string;
}

interface PricingInfo {
  game_type_id: number;
  max_players: number;
  billing_period: string;
  price: number;
}

interface CouponInfo {
  id: number;
  code: string;
  discount_type: string;
  discount_value: number;
}

const CreateServer: React.FC = () => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const { user } = useAuth();
  const [gameTypes, setGameTypes] = useState<GameType[]>([]);
  const [nodes, setNodes] = useState<ServerNode[]>([]);
  const [pricingInfo, setPricingInfo] = useState<PricingInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedGameType, setSelectedGameType] = useState<GameType | null>(null);
  const [selectedNode, setSelectedNode] = useState<ServerNode | null>(null);
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState<string>('');
  const [selectedMaxPlayers, setSelectedMaxPlayers] = useState<number | null>(null);
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponValidation, setCouponValidation] = useState<CouponInfo | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [showProgress, setShowProgress] = useState(false);
  const [installingServerId, setInstallingServerId] = useState<number | null>(null);
  const [socket, setSocket] = useState<any>(null);
  const [modSupportEnabled, setModSupportEnabled] = useState(false);
  const [modSupportPrice, setModSupportPrice] = useState(20); // 月费
  const [paymentConfig, setPaymentConfig] = useState<any>(null);
  const [rainbowPayConfig, setRainbowPayConfig] = useState<any>(null);
  const [pageBackgroundImage, setPageBackgroundImage] = useState<string>('');


  useEffect(() => {
    loadGameTypes();
    loadNodes();
    loadPaymentConfig();
    
    // 使用全局Socket.IO连接，避免重复连接
    const globalSocket = (window as any).io;
    if (globalSocket) {
      setSocket(globalSocket);
      console.log('使用全局Socket.IO连接:', globalSocket.id);
    } else {
      console.warn('全局Socket.IO连接不可用');
    }
  }, []);

  // 当彩虹易支付配置加载后，设置默认支付方式
  useEffect(() => {
    if (rainbowPayConfig) {
      console.log('🌈 彩虹易支付配置:', rainbowPayConfig);
      const defaultPaymentMethod = 
        rainbowPayConfig.wechat_enabled ? "rainbow_pay_wechat" :
        rainbowPayConfig.alipay_enabled ? "rainbow_pay_alipay" :
        rainbowPayConfig.qq_enabled ? "rainbow_pay_qq" : "balance";
      
      console.log('🎯 设置默认支付方式:', defaultPaymentMethod);
      form.setFieldValue('payment_method', defaultPaymentMethod);
      
      // 强制更新表单显示
      setTimeout(() => {
        const currentValue = form.getFieldValue('payment_method');
        console.log('📋 当前表单支付方式值:', currentValue);
      }, 100);
    }
  }, [rainbowPayConfig, form]);

  // 当选择游戏类型后，更新页面背景
  useEffect(() => {
    if (selectedGameType?.background_image_url) {
      console.log('🎮 设置游戏背景图片:', selectedGameType.background_image_url);
      setPageBackgroundImage(selectedGameType.background_image_url);
    } else {
      // 如果没有背景图片，清除背景
      setPageBackgroundImage('');
    }
  }, [selectedGameType]);

  const loadGameTypes = async () => {
    try {
      const response = await api.get('/games/types');
      setGameTypes(response.data);
    } catch (error) {
      message.error('加载游戏类型失败');
    }
  };

  const loadPaymentConfig = async () => {
    try {
      const response = await api.get('/payment/rainbow/config');
      console.log('🌈 获取到的支付配置:', response.data);
      setPaymentConfig(response.data);
      setRainbowPayConfig(response.data);
    } catch (error) {
      console.error('获取支付配置失败:', error);
    }
  };

  const loadNodes = async () => {
    try {
      const response = await api.get('/servers/nodes');
      setNodes(response.data);
    } catch (error) {
      message.error('加载节点失败');
    }
  };

  const loadPricingData = async (gameTypeId: number) => {
    try {
      // 首先尝试使用公开的价格API（普通用户可访问）
      const response = await api.get(`/admin/pricing/public/game/${gameTypeId}`);
      setPricingInfo(response.data);
      console.log('加载价格数据:', response.data);
    } catch (error: any) {
      console.error('加载价格数据失败:', error);
      // 如果公开API失败，尝试管理员API
      if (error.response?.status === 403 || error.response?.status === 404) {
        try {
          const adminResponse = await api.get(`/admin/pricing/game/${gameTypeId}`);
          setPricingInfo(adminResponse.data);
          console.log('使用管理员API加载价格数据:', adminResponse.data);
        } catch (adminError: any) {
          console.log('用户无管理员权限，使用默认价格计算');
          setPricingInfo([]);
        }
      } else {
        console.log('价格API不可用，使用默认价格计算');
        setPricingInfo([]);
      }
    }
  };


  const validateCoupon = async (code: string) => {
    if (!code) {
      setCouponValidation(null);
      return;
    }

    try {
      const response = await api.post('/admin/coupons/validate', {
        code,
        game_type_id: selectedGameType?.id,
        billing_period: selectedBillingPeriod
      });
      setCouponValidation(response.data.coupon);
      message.success('优惠码验证成功');
    } catch (error: any) {
      setCouponValidation(null);
      message.error(error.response?.data?.message || '优惠码验证失败');
    }
  };

  // 将数字天数转换为后端期望的周期名称
  const getBillingPeriodName = (days: string) => {
    const dayNum = parseInt(days);
    if (dayNum <= 7) return 'weekly';
    if (dayNum <= 30) return 'monthly';
    if (dayNum <= 90) return 'quarterly';
    if (dayNum <= 180) return 'semiannual';
    return 'yearly';
  };

  // 将计费周期名称转换为天数
  const getDaysFromBillingPeriod = (billingPeriod: string) => {
    switch (billingPeriod) {
      case 'weekly': return 7;
      case 'monthly': return 30;
      case 'quarterly': return 90;
      case 'semiannual': return 180;
      case 'yearly': return 365;
      default: return 30; // 默认30天
    }
  };

  // 计算MOD支持价格
  const calculateModPrice = (days: number) => {
    const modSupportMonthlyPrice = 20.0; // MOD支持月费20元
    
    if (days >= 30) {
      // 30天或以上，按整月计算
      return Math.ceil(days / 30) * modSupportMonthlyPrice;
    } else {
      // 少于30天，按比例计算（每天约0.67元）
      return (days / 30) * modSupportMonthlyPrice;
    }
  };

  const getCurrentPrice = () => {
    if (!selectedGameType || !selectedMaxPlayers || !selectedBillingPeriod) {
      return 0;
    }

    // 只使用后端价格数据
    if (pricingInfo.length > 0) {
      const pricing = pricingInfo.find(p => 
        p.game_type_id === selectedGameType.id &&
        p.max_players === selectedMaxPlayers &&
        p.billing_period === getBillingPeriodName(selectedBillingPeriod)
      );
      
      if (pricing) {
        console.log('使用后端价格数据:', pricing);
        const basePrice = Number(pricing.price) || 0;
        const modPrice = modSupportEnabled ? calculateModPrice(Number(selectedBillingPeriod)) : 0;
        return basePrice + modPrice;
      }
    }

    // 如果没有找到对应的价格数据，返回0
    console.log('未找到对应的价格数据');
    return 0;
  };


  const calculateFinalPrice = () => {
    const basePrice = getCurrentPrice();
    const discountAmount = couponValidation ? 
      (couponValidation.discount_type === 'percentage' ? 
        basePrice * (couponValidation.discount_value / 100) : 
        couponValidation.discount_value) : 0;
    
    return Number(basePrice) - Number(discountAmount);
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    
    try {
      console.log('onFinish接收到的values:', values);
      console.log('保存的formData:', formData);
      console.log('当前状态变量:', {
        selectedGameType: selectedGameType?.id,
        selectedNode: selectedNode?.id,
        selectedMaxPlayers,
        selectedBillingPeriod,
        couponCode,
        paymentMethod: values.payment_method
      });

      // 将数字天数转换为后端期望的周期名称
      const getBillingPeriodName = (days: string) => {
        const dayNum = parseInt(days);
        if (dayNum <= 7) return 'weekly';
        if (dayNum <= 30) return 'monthly';
        if (dayNum <= 90) return 'quarterly';
        if (dayNum <= 180) return 'semiannual';
        return 'yearly';
      };

      const finalPrice = calculateFinalPrice();
      const paymentMethod = values.payment_method || (
        rainbowPayConfig?.wechat_enabled ? 'rainbow_pay_wechat' :
        rainbowPayConfig?.alipay_enabled ? 'rainbow_pay_alipay' :
        rainbowPayConfig?.qq_enabled ? 'rainbow_pay_qq' : 'balance'
      );

      // 检查余额是否足够（如果选择余额支付）
      if (paymentMethod === 'balance') {
        const userBalance = user?.balance || 0;
        if (userBalance < finalPrice) {
          message.error(`余额不足，当前余额: ¥${userBalance.toFixed(2)}，需要: ¥${finalPrice.toFixed(2)}`);
          setLoading(false);
          return;
        }
      }

      // 处理彩虹易支付方式
      let actualPaymentMethod = paymentMethod;
      if (paymentMethod.startsWith('rainbow_pay_')) {
        actualPaymentMethod = 'rainbow_pay';
      }

      // 合并所有步骤的数据
      const finalFormData = {
        ...formData,  // 步骤0的数据
        ...values,    // 步骤1的数据
        billing_period: getBillingPeriodName(selectedBillingPeriod), // 转换为周期名称
        coupon_code: couponCode || null,
        mod_support_enabled: modSupportEnabled ? 1 : 0,
        payment_method: actualPaymentMethod,
        payment_type: paymentMethod, // 保存具体的支付类型
        total_price: finalPrice
      };

      console.log('发送的服务器创建数据:', finalFormData);

      // 如果选择彩虹易支付，先创建支付订单
      if (actualPaymentMethod === 'rainbow_pay') {
        const paymentResponse = await api.post('/payment/rainbow/create-order', {
          amount: finalPrice,
          type: 'server_purchase',
          description: `购买${selectedGameType?.display_name}服务器 - ${selectedMaxPlayers}人 - ${selectedBillingPeriod}天`,
          return_url: `${window.location.origin}/payment/notify`,
          server_data: finalFormData // 将服务器数据传递给支付回调
        });

        if (paymentResponse.data.success) {
          // 显示美化的确认对话框
          Modal.confirm({
            title: (
              <div style={{ textAlign: 'center', padding: '20px 50px 20px 24px' }}>
                <div style={{ 
                  fontSize: '24px', 
                  color: '#1890ff', 
                  marginBottom: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  💳 确认支付
                </div>
              </div>
            ),
            content: (
              <div style={{ padding: '20px 50px 20px 24px' }}>
                {/* 订单信息卡片 */}
                <Card 
                  size="small" 
                  style={{ 
                    marginBottom: '20px',
                    background: 'linear-gradient(135deg, #f6f9ff 0%, #e6f3ff 100%)',
                    border: '1px solid #d6e4ff',
                    borderRadius: '12px'
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold', 
                      color: '#1890ff',
                      marginBottom: '8px'
                    }}>
                      ✅ 订单创建成功
                    </div>
                    <div style={{ 
                      fontSize: '28px', 
                      fontWeight: 'bold', 
                      color: '#52c41a',
                      marginBottom: '4px'
                    }}>
                      ¥{finalPrice}
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666'
                    }}>
                      服务器购买费用
                    </div>
                  </div>
                </Card>

                {/* 支付流程说明 */}
                <div style={{ 
                  background: '#f8f9fa', 
                  borderRadius: '8px', 
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: '#2c3e50',
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>
                    🚀 支付流程
                  </div>
                  <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#1890ff', fontWeight: 'bold' }}>1.</span> 点击确认后跳转到支付页面
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#1890ff', fontWeight: 'bold' }}>2.</span> 选择支付方式完成支付
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#1890ff', fontWeight: 'bold' }}>3.</span> 支付成功后自动返回并开始创建服务器
                    </div>
                    <div>
                      <span style={{ color: '#1890ff', fontWeight: 'bold' }}>4.</span> 服务器创建完成后可立即使用
                    </div>
                  </div>
                </div>

                {/* 温馨提示 */}
                <div style={{ 
                  background: '#fff7e6', 
                  border: '1px solid #ffd591',
                  borderRadius: '8px', 
                  padding: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#d46b08',
                    fontWeight: '500'
                  }}>
                    温馨提示：支付成功后服务器将自动创建并开始安装，请耐心等待
                  </div>
                </div>
              </div>
            ),
            okText: (
              <span style={{ fontWeight: 'bold' }}>
                💳 确认支付
              </span>
            ),
            cancelText: '取消',
            okButtonProps: {
              style: {
                background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                border: 'none',
                borderRadius: '8px',
                height: '40px',
                fontSize: '16px',
                fontWeight: 'bold'
              }
            },
            cancelButtonProps: {
              style: {
                borderRadius: '8px',
                height: '40px',
                fontSize: '16px'
              }
            },
            width: 480,
            centered: false,
            style: {
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              margin: 0
            },
            onOk: () => {
              // 跳转到支付页面
              const form = document.createElement('form');
              form.method = 'POST';
              form.action = paymentResponse.data.payUrl;
              form.target = '_blank';

              Object.keys(paymentResponse.data.payParams).forEach(key => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = paymentResponse.data.payParams[key];
                form.appendChild(input);
              });

              document.body.appendChild(form);
              form.submit();
              document.body.removeChild(form);

              message.success('正在跳转到支付页面，支付成功后服务器将自动创建...');
            },
            onCancel: () => {
              message.info('支付已取消');
            }
          });
          setLoading(false);
          return;
        } else {
          message.error(paymentResponse.data.message || '创建支付订单失败');
          setLoading(false);
          return;
        }
      }

      // 先设置进度条监听器
      let serverId: number | null = null;
      
      const handleInstallProgress = (data: any) => {
        console.log('收到安装进度:', data);
        
        // 如果是第一次收到进度更新，显示进度条
        if (!showProgress && data.serverId) {
          serverId = data.serverId;
          setInstallingServerId(data.serverId);
          setShowProgress(true);
          console.log('显示进度条:', { serverId: data.serverId, serverName: formData.name });
        }
        
        // 检查是否完成
        if (data.status === 'success') {
          // 不自动关闭进度条，让用户点击按钮
          message.success('服务器创建成功！');
          socket?.off('server-install-progress', handleInstallProgress);
        } else if (data.status === 'error') {
          setShowProgress(false);
          message.error(data.message || '服务器创建失败');
          socket?.off('server-install-progress', handleInstallProgress);
        }
      };
      
      if (socket) {
        socket.on('server-install-progress', handleInstallProgress);
        
        // 清理监听器
        setTimeout(() => {
          socket.off('server-install-progress', handleInstallProgress);
        }, 300000); // 5分钟后清理
      }

      // 发送API请求（增加超时时间到60秒）
      const response = await api.post('/servers-new', finalFormData, {
        timeout: 60000 // 60秒超时
      });
      
      console.log('API响应:', response.data);
      
      // 如果API立即返回了serverId，也设置进度条
      if (response.data.serverId && !showProgress) {
        serverId = response.data.serverId;
        setInstallingServerId(response.data.serverId);
        setShowProgress(true);
        console.log('API响应后显示进度条:', { serverId: response.data.serverId, serverName: formData.name });
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '创建服务器失败');
    } finally {
      setLoading(false);
    }
  };

  const next = () => {
    form.validateFields().then((values) => {
      console.log('步骤切换时的表单值:', values);
      // 保存当前步骤的数据到formData
      setFormData((prev: any) => ({ ...prev, ...values }));
      setCurrentStep(currentStep + 1);
    }).catch((errorInfo) => {
      console.log('表单验证失败:', errorInfo);
      message.error('请完成当前步骤的所有必填字段');
    });
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <Form.Item
              name="name"
              label="服务器名称"
              rules={[{ required: true, message: '请输入服务器名称' }]}
            >
              <Input placeholder="输入服务器名称" size="large" />
            </Form.Item>

            <Form.Item
              name="game_type_id"
              label="游戏类型"
              rules={[{ required: true, message: '请选择游戏类型' }]}
            >
              <Select
                placeholder="选择游戏类型"
                size="large"
                onChange={(value) => {
                  const gameType = gameTypes.find(gt => gt.id === value);
                  setSelectedGameType(gameType || null);
                  form.setFieldValue('game_type_id', value);
                  if (gameType) {
                    loadPricingData(gameType.id);
                  }
                }}
              >
                {gameTypes.map(gameType => (
                  <Option key={gameType.id} value={gameType.id}>
                    <div className="flex items-center space-x-3">
                      <Image
                        src={gameType.icon || '/images/game-icons/placeholder_184x69.jpg'}
                        alt={gameType.display_name}
                        width={74}
                        height={28}
                        style={{ objectFit: 'cover', borderRadius: '4px' }}
                        fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCA0MCAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjE2IiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjIwIiB5PSI4IiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkdhbWU8L3RleHQ+Cjwvc3ZnPg=="
                      />
                      <div className="flex flex-col">
                        <span className="font-medium">{gameType.display_name}</span>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          {gameType.is_steam_game && <span className="text-blue-500">[Steam]</span>}
                          {gameType.supports_mods && <span className="text-green-500">[MOD]</span>}
                        </div>
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {selectedGameType && (
              <Alert
                message={
                  <div className="flex items-center space-x-3">
                    <Image
                      src={selectedGameType.icon || '/images/game-icons/placeholder_184x69.jpg'}
                      alt={selectedGameType.display_name}
                      width={100}
                      height={40}
                      style={{ objectFit: 'cover', borderRadius: '6px' }}
                      fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCA2MCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjMwIiB5PSIxMiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+R2FtZTwvdGV4dD4KPC9zdmc+"
                    />
                    <div>
                      <div className="font-medium">{selectedGameType.display_name}</div>
                      <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                        {selectedGameType.is_steam_game && <span className="text-blue-500">[Steam]</span>}
                        {selectedGameType.supports_mods && <span className="text-green-500">[MOD]</span>}
                      </div>
                    </div>
                  </div>
                }
                description={selectedGameType.description}
                type="info"
                showIcon={false}
              />
            )}

            <Form.Item
              name="node_id"
              label="服务器节点"
              rules={[{ required: true, message: '请选择服务器节点' }]}
            >
              <Select
                placeholder="选择服务器节点"
                size="large"
                onChange={(value) => {
                  const node = nodes.find(n => n.id === value);
                  setSelectedNode(node || null);
                  form.setFieldValue('node_id', value);
                }}
              >
                {nodes.map(node => (
                  <Option key={node.id} value={node.id}>
                    <div className="flex items-center justify-between">
                      <span>{node.name} {node.is_local ? '(本地)' : '(远程)'}</span>
                      <span className="text-gray-500 text-sm">{node.host}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {selectedNode && (
              <Alert
                message="节点信息"
                description={`服务器路径: ${selectedNode.server_path}`}
                type="success"
                showIcon
              />
            )}
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Form.Item
                name="max_players"
                label="最大玩家数"
                rules={[{ required: true, message: '请选择最大玩家数' }]}
              >
                <Select
                  placeholder="选择最大玩家数"
                  size="large"
                  onChange={(value) => {
                    setSelectedMaxPlayers(value);
                    form.setFieldValue('max_players', value);
                  }}
                >
                  {pricingInfo.length > 0 ? (
                    // 使用后端价格数据中的最大玩家数
                    Array.from(new Set(
                      pricingInfo
                        .filter(p => p.game_type_id === selectedGameType?.id)
                        .map(p => p.max_players)
                    )).sort((a, b) => a - b).map(players => (
                      <Option key={players} value={players}>
                        {players}人
                      </Option>
                    ))
                  ) : (
                    // 如果没有后端价格数据，显示提示
                    <Option value={null} disabled>
                      请先选择游戏类型
                    </Option>
                  )}
                </Select>
              </Form.Item>

              <Form.Item
                name="billing_period"
                label="购买时长"
                rules={[{ required: true, message: '请选择购买时长' }]}
              >
                <Select
                  placeholder="选择购买时长"
                  size="large"
                  onChange={(value) => {
                    setSelectedBillingPeriod(value);
                    form.setFieldValue('billing_period', value);
                  }}
                >
                  {pricingInfo.length > 0 && selectedMaxPlayers ? (
                    // 使用后端价格数据
                    pricingInfo
                      .filter(p => p.game_type_id === selectedGameType?.id && p.max_players === selectedMaxPlayers)
                      .map(pricing => {
                        const days = getDaysFromBillingPeriod(pricing.billing_period);
                        const basePrice = Number(pricing.price) || 0;
                        const modPrice = modSupportEnabled ? calculateModPrice(days) : 0;
                        const totalPrice = basePrice + modPrice;
                        
                        return (
                          <Option key={pricing.billing_period} value={days.toString()}>
                            {days}天 - ¥{Number(totalPrice).toFixed(2)}
                          </Option>
                        );
                      })
                  ) : (
                    // 如果没有后端价格数据或未选择最大玩家数，显示提示
                    <Option value={null} disabled>
                      {!selectedMaxPlayers ? '请先选择最大玩家数' : '暂无价格数据'}
                    </Option>
                  )}
                </Select>
              </Form.Item>
            </div>

            {selectedGameType?.supports_mods && (
              <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                <Row gutter={16} align="middle">
                  <Col span={16}>
                    <div>
                      <h4 className="font-medium text-yellow-800 mb-1">MOD支持功能</h4>
                      <p className="text-yellow-600 text-sm mb-0">
                        启用后可安装Steam Workshop MOD，月费¥{modSupportPrice}
                      </p>
                    </div>
                  </Col>
                  <Col span={8}>
                    <Switch
                      checked={modSupportEnabled}
                      onChange={setModSupportEnabled}
                      checkedChildren="启用"
                      unCheckedChildren="禁用"
                    />
                  </Col>
                </Row>
              </div>
            )}

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">价格说明</h4>
              <div className="text-blue-600 text-sm space-y-1">
                <p>• 价格基于服务器配置和购买时长计算</p>
                {modSupportEnabled && (
                  <div>
                    <p>• MOD支持：每月 ¥{modSupportPrice}.00</p>
                    <p>• 少于30天按比例计算，30天以上按整月计算</p>
                  </div>
                )}
                <p>• 具体价格请选择配置后查看</p>
              </div>
            </div>

            <Form.Item
              name="coupon_code"
              label="优惠码 (可选)"
            >
              <div className="flex space-x-2">
                <Input
                  placeholder="输入优惠码"
                  size="large"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                />
                <Button
                  size="large"
                  onClick={() => validateCoupon(couponCode)}
                  disabled={!couponCode}
                >
                  验证
                </Button>
              </div>
            </Form.Item>

            {couponValidation && (
              <Alert
                message="优惠码验证成功"
                description={`折扣: ${
                  couponValidation.discount_type === 'percentage' 
                    ? `${couponValidation.discount_value}%` 
                    : `¥${couponValidation.discount_value}`
                }`}
                type="success"
                showIcon
              />
            )}

            {selectedMaxPlayers && selectedBillingPeriod && getCurrentPrice() > 0 && (
              <Card>
                <div className="text-center">
                  <h3 className="text-lg font-medium mb-3">价格详情</h3>
                  
                  {/* 配置信息 */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">
                      {selectedMaxPlayers}人服务器 - {selectedBillingPeriod}天
                      {modSupportEnabled && <span className="text-green-600"> (含MOD支持)</span>}
                    </p>
                  </div>

                  {/* 价格对比 */}
                  <div className="flex justify-center items-center space-x-6">
                    {/* 原价 */}
                    <div className="text-center">
                      <p className="text-sm text-gray-500 mb-1">原价</p>
                      <div className="text-xl font-bold text-gray-600">
                        ¥{getCurrentPrice().toFixed(2)}
                      </div>
                    </div>

                    {/* 箭头 */}
                    {couponValidation && (
                      <div className="text-gray-400">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>
                    )}

                    {/* 最终价格 */}
                    <div className="text-center">
                      <p className="text-sm text-gray-500 mb-1">最终价格</p>
                      <div className="text-3xl font-bold text-green-600">
                        ¥{Number(calculateFinalPrice()).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {/* 优惠信息 */}
                  {couponValidation && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex justify-center items-center space-x-4">
                        <div className="text-center">
                          <p className="text-sm text-green-600 font-medium">优惠金额</p>
                          <p className="text-lg font-bold text-green-700">
                            ¥{(getCurrentPrice() - calculateFinalPrice()).toFixed(2)}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-green-600 font-medium">折扣率</p>
                          <p className="text-lg font-bold text-green-700">
                            {couponValidation.discount_type === 'percentage' 
                              ? `${couponValidation.discount_value}%` 
                              : '固定金额'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}

            <Form.Item
              name="payment_method"
              label="支付方式"
              rules={[{ required: true, message: '请选择支付方式' }]}
            >
              <div>
                <Title level={4} style={{ marginBottom: '16px' }}>选择支付方式</Title>
                
                <Radio.Group 
                  style={{ width: '100%' }}
                >
                  {/* 彩虹易支付选项 */}
                  {rainbowPayConfig?.enabled && (
                    <Card 
                      size="small" 
                      style={{ 
                        marginBottom: '16px',
                        border: '2px solid #1890ff',
                        borderRadius: '8px'
                      }}
                    >
                        <Row gutter={[16, 16]} justify="center">
                          <Col span={8}>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              <Radio 
                                value="rainbow_pay_wechat" 
                                disabled={!rainbowPayConfig?.wechat_enabled}
                                style={{ width: 'auto' }}
                              >
                                <div style={{ textAlign: 'center', padding: '12px' }}>
                                  <WechatOutlined 
                                    style={{ 
                                      fontSize: '24px', 
                                      color: '#07c160', 
                                      marginBottom: '8px', 
                                      display: 'block' 
                                    }} 
                                  />
                                  <div style={{ 
                                    fontWeight: 'bold'
                                  }}>
                                    微信支付
                                  </div>
                                  <div style={{ 
                                    fontSize: '12px', 
                                    color: '#666'
                                  }}>
                                    {rainbowPayConfig?.wechat_enabled ? '安全便捷' : '未启用'}
                                  </div>
                                </div>
                              </Radio>
                            </div>
                          </Col>
                          <Col span={8}>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              <Radio 
                                value="rainbow_pay_alipay" 
                                disabled={!rainbowPayConfig?.alipay_enabled}
                                style={{ width: 'auto' }}
                              >
                                <div style={{ textAlign: 'center', padding: '12px' }}>
                                  <AlipayOutlined 
                                    style={{ 
                                      fontSize: '24px', 
                                      color: '#1677ff', 
                                      marginBottom: '8px', 
                                      display: 'block' 
                                    }} 
                                  />
                                  <div style={{ 
                                    fontWeight: 'bold'
                                  }}>
                                    支付宝
                                  </div>
                                  <div style={{ 
                                    fontSize: '12px', 
                                    color: '#666'
                                  }}>
                                    {rainbowPayConfig?.alipay_enabled ? '快速到账' : '未启用'}
                                  </div>
                                </div>
                              </Radio>
                            </div>
                          </Col>
                          <Col span={8}>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              <Radio 
                                value="rainbow_pay_qq" 
                                disabled={!rainbowPayConfig?.qq_enabled}
                                style={{ width: 'auto' }}
                              >
                                <div style={{ textAlign: 'center', padding: '12px' }}>
                                  <QqOutlined 
                                    style={{ 
                                      fontSize: '24px', 
                                      color: '#12b7f5', 
                                      marginBottom: '8px', 
                                      display: 'block' 
                                    }} 
                                  />
                                  <div style={{ 
                                    fontWeight: 'bold'
                                  }}>
                                    QQ钱包
                                  </div>
                                  <div style={{ 
                                    fontSize: '12px', 
                                    color: '#666'
                                  }}>
                                    {rainbowPayConfig?.qq_enabled ? '简单易用' : '未启用'}
                                  </div>
                                </div>
                              </Radio>
                            </div>
                          </Col>
                        </Row>
                    </Card>
                  )}

                  {/* 余额支付选项 */}
                  <Card 
                    size="small" 
                    style={{ 
                      border: '1px solid #d9d9d9',
                      borderRadius: '8px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Radio value="balance" style={{ width: 'auto' }}>
                        <div style={{ textAlign: 'center', padding: '12px' }}>
                          <DollarOutlined style={{ fontSize: '24px', color: '#52c41a', marginBottom: '8px', display: 'block' }} />
                          <div style={{ fontWeight: 'bold' }}>余额支付</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            当前余额: ¥{user?.balance || 0}
                          </div>
                        </div>
                      </Radio>
                    </div>
                  </Card>
                </Radio.Group>

                {!rainbowPayConfig?.enabled && (
                  <Alert
                    message="彩虹易支付未启用"
                    description="请联系管理员启用在线支付功能"
                    type="warning"
                    style={{ marginTop: '16px' }}
                  />
                )}
              </div>
            </Form.Item>
          </div>
        );

      default:
        return null;
    }
  };

  const steps = [
    {
      title: '基本信息',
      icon: <PlusOutlined />,
    },
    {
      title: '价格确认',
      icon: <DollarOutlined />,
    },
  ];

  return (
    <div 
      className="fade-in" 
      style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'flex-start', 
        minHeight: '100vh', 
        padding: '20px',
        backgroundImage: pageBackgroundImage ? `url(${pageBackgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      <div style={{ width: '100%', maxWidth: '800px' }}>
        <Card 
          style={{
            backgroundColor: pageBackgroundImage ? 'rgba(255, 255, 255, 0.95)' : '#ffffff',
            backdropFilter: pageBackgroundImage ? 'blur(10px)' : 'none',
            border: pageBackgroundImage ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #d9d9d9',
            boxShadow: pageBackgroundImage ? '0 8px 32px rgba(0, 0, 0, 0.1)' : '0 1px 2px -2px rgba(0, 0, 0, 0.16), 0 3px 6px 0 rgba(0, 0, 0, 0.12), 0 5px 12px 4px rgba(0, 0, 0, 0.09)'
          }}
        >
          <h2 className="text-2xl font-bold mb-6" style={{ textAlign: 'center' }}>创建服务器</h2>
          
          <Steps current={currentStep} className="mb-8">
            {steps.map((item, index) => (
              <Step key={index} title={item.title} icon={item.icon} />
            ))}
          </Steps>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onValuesChange={(changedValues, allValues) => {
              console.log('表单值变化:', { changedValues, allValues });
            }}
            className="max-w-4xl"
          >
          <div className="min-h-96">
            {renderStepContent()}
          </div>

          <Divider />

          <div className="flex justify-between">
            {currentStep > 0 && (
              <Button onClick={prev}>
                上一步
              </Button>
            )}
            
            <div className="ml-auto">
              {currentStep < steps.length - 1 && (
                <Button type="primary" onClick={next}>
                  下一步
                </Button>
              )}
              
              {currentStep === steps.length - 1 && (
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  disabled={!selectedGameType || !selectedNode || !selectedMaxPlayers || !selectedBillingPeriod}
                >
                  创建服务器
                </Button>
              )}
            </div>
          </div>
        </Form>
        </Card>
      </div>
      
      <ServerInstallProgress
        visible={showProgress}
        onClose={() => setShowProgress(false)}
        serverId={installingServerId}
        serverName={formData.name}
        socket={socket}
      />
    </div>
  );
};

export default CreateServer;
